import * as React from 'react'

import { inject, observer } from 'mobx-react'
import { Redirect, RouteComponentProps, Switch, withRouter } from 'react-router'

import { Loader } from '~/client/src/shared/components/Loader'
import WrappedRoute from '~/client/src/shared/components/WrappedRoute'
import commonRoutes from '~/client/src/shared/constants/commonRoutes'
import IRoute from '~/client/src/shared/interfaces/IRoute'
import CommonStore from '~/client/src/shared/stores/ui/Common.store'

import LoginView from './views/LoginView'

const InfoView = React.lazy(
  () => import('~/client/src/shared/views/InfoView/InfoView'),
)
const PasswordResetView = React.lazy(() => import('./views/PasswordResetView'))
const Redirector = React.lazy(() => import('./views/Redirector/Redirector'))
const SignUpView = React.lazy(() => import('./views/SignUpView'))
const SSOAdminConsentView = React.lazy(
  () => import('./views/SSOAdminConsentView'),
)
const SSOCallbackView = React.lazy(() => import('./views/SSOCallbackView'))
const TenantRoutingView = React.lazy(() => import('./views/TenantRoutingView'))

/**
 * The retired `/projects` picker URL redirects to the portal's canonical
 * `/portal` route, preserving any `return_to` on the query so the gate can
 * still fly a deep-link over to its tenant.
 */
const ProjectsToPortalRedirect = () => (
  <Redirect to={{ pathname: commonRoutes.PORTAL, search: location.search }} />
)

const routes: IRoute[] = [
  {
    path: commonRoutes.LOGIN,
    component: LoginView,
  },
  {
    path: commonRoutes.SSO_CALLBACK,
    component: SSOCallbackView,
  },
  {
    path: commonRoutes.SSO_ADMIN_CONSENT,
    component: SSOAdminConsentView,
  },
  {
    path: commonRoutes.SIGN_UP,
    component: SignUpView,
  },
  {
    // Retired picker URL → canonical /portal (preserving return_to).
    path: commonRoutes.PROJECTS,
    component: ProjectsToPortalRedirect,
  },
  {
    // The User Portal is the post-login landing. TenantRoutingView gates it:
    // no session → /login, a return_to deep-link → fly over to the tenant,
    // else render the portal.
    path: commonRoutes.PORTAL,
    component: TenantRoutingView,
  },
  {
    path: commonRoutes.REDIRECTOR,
    component: Redirector,
  },
  {
    path: commonRoutes.RESET_PASSWORD,
    component: PasswordResetView,
  },
  {
    path: commonRoutes.INFO,
    component: InfoView,
  },
]

interface IProps extends RouteComponentProps {
  common?: CommonStore
}

@inject('common')
@observer
class LoginRoutes extends React.Component<IProps> {
  public constructor(props: IProps) {
    super(props)
    props.common.initHistory(props.history)
  }

  public render() {
    return (
      <main className="tw:h-full">
        <React.Suspense
          fallback={
            <Loader className="tw:flex tw:h-full tw:items-center tw:justify-center" />
          }
        >
          <Switch>
            {routes.map(route => (
              <WrappedRoute
                key={route.path}
                defaultViewToDisplay={commonRoutes.LOGIN}
                {...route}
              />
            ))}
            {/*
             * Preserve the query string on the catch-all redirect to /login
             * (STX1-540). The unauth bounce can land on an unmatched path
             * (e.g. `/?return_to=…`); dropping the search here would lose the
             * `return_to` destination — the URL is its only carrier now that
             * the sessionStorage cache is gone. A bare `/login` (no return_to)
             * still redirects cleanly.
             */}
            <Redirect
              to={{
                pathname: commonRoutes.LOGIN,
                search: this.props.location.search,
              }}
            />
          </Switch>
        </React.Suspense>
      </main>
    )
  }
}

export default withRouter(LoginRoutes)
